import React, { useState } from 'react';
import { Save, RotateCcw, Clock, Settings as SettingsIcon, Link2, Bell } from 'lucide-react';
import { useSettings, useUpdateSettings, useRollbackSetting, useAuditLogs } from '@/hooks/useApi';
import { Card, Badge, LoadingSpinner, Modal, EmptyState } from '@/components/UI';
import { ExchangeConnectionPanel } from '@/components/ExchangeConnection';
import { NotificationSettings } from '@/components/NotificationSettings';
import { formatDateTime } from '@/utils';
import type { Setting, AuditLogEntry } from '@/types';

export function SettingsPage() {
  const { data: settings, isLoading } = useSettings();
  const updateSettings = useUpdateSettings();
  const rollbackSetting = useRollbackSetting();
  const { data: auditLogsData } = useAuditLogs({ limit: 20 });
  const auditLogs = (auditLogsData as any)?.items ?? auditLogsData ?? [];

  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [showAudit, setShowAudit] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'general' | 'exchange' | 'notifications'>('general');

  if (isLoading) return <LoadingSpinner text="Loading settings..." />;

  const settingsList = (settings ?? []) as Setting[];
  const categories = ['all', ...new Set(settingsList.map((s) => s.category))];
  const filtered = activeCategory === 'all'
    ? settingsList
    : settingsList.filter((s) => s.category === activeCategory);

  const handleSave = async () => {
    const updates = Object.entries(editedValues).map(([key, value]) => ({ key, value }));
    if (updates.length === 0) return;
    try {
      await updateSettings.mutateAsync(updates);
      setEditedValues({});
    } catch { /* handled */ }
  };

  const handleRollback = async (auditLogId: string) => {
    try {
      await rollbackSetting.mutateAsync(auditLogId);
    } catch { /* handled */ }
  };

  const tabs = [
    { key: 'general', label: 'General', icon: SettingsIcon },
    { key: 'exchange', label: 'Exchanges & Live Mode', icon: Link2 },
    { key: 'notifications', label: 'Notifications', icon: Bell },
  ] as const;

  return (
    <div className="space-y-6 animate-in">
      <h1 className="text-2xl font-bold text-surface-100">Settings</h1>

      {/* Tabs */}
      <div className="flex gap-2">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === key ? 'bg-brand-600 text-white' : 'bg-surface-800 text-surface-400 hover:bg-surface-700'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {activeTab === 'general' && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-surface-200">General Settings</h2>
            <div className="flex items-center gap-2">
              {Object.keys(editedValues).length > 0 && (
                <button onClick={handleSave} className="btn-primary btn-sm">
                  <Save className="w-4 h-4" /> Save Changes ({Object.keys(editedValues).length})
                </button>
              )}
              <button onClick={() => setShowAudit(true)} className="btn-secondary btn-sm">
                <Clock className="w-4 h-4" /> Audit Log
              </button>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  activeCategory === cat
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface-800 text-surface-400 hover:bg-surface-700'
                }`}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>

          {/* Settings Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((setting: Setting) => (
              <Card key={setting.key}>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-surface-200">{setting.key}</label>
                    <Badge variant="brand" className="text-[10px]">{setting.category}</Badge>
                  </div>
                  <p className="text-xs text-surface-500">{setting.description}</p>
                  <input
                    className="input"
                    value={editedValues[setting.key] ?? setting.value}
                    onChange={(e) =>
                      setEditedValues({
                        ...editedValues,
                        [setting.key]: e.target.value,
                      })
                    }
                  />
                  <p className="text-[10px] text-surface-600">
                    Updated: {formatDateTime(setting.updated_at)} by {setting.updated_by}
                  </p>
                </div>
              </Card>
            ))}
          </div>

          {/* Audit Log Modal */}
          <Modal isOpen={showAudit} onClose={() => setShowAudit(false)} title="Settings Audit Log" size="lg">
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {!auditLogs || (auditLogs as unknown[]).length === 0 ? (
                <EmptyState title="No audit entries" />
              ) : (
                (auditLogs as AuditLogEntry[]).map((log: AuditLogEntry) => (
                  <div key={log.id} className="p-3 rounded-lg bg-surface-800/50 border border-surface-700">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-surface-200">{log.action}</span>
                      <button
                        onClick={() => handleRollback(log.id)}
                        className="text-xs text-brand-400 hover:text-brand-300 flex items-center gap-1"
                      >
                        <RotateCcw className="w-3 h-3" /> Rollback
                      </button>
                    </div>
                    <p className="text-xs text-surface-400">
                      {log.entity_type}: {log.entity_id}
                    </p>
                    {log.old_value && (
                      <div className="mt-1 text-xs">
                        <span className="text-loss">Old: </span>
                        <span className="text-surface-500">{log.old_value}</span>
                        <span className="text-surface-600 mx-2">→</span>
                        <span className="text-profit">New: </span>
                        <span className="text-surface-500">{log.new_value}</span>
                      </div>
                    )}
                    <p className="text-[10px] text-surface-600 mt-1">{formatDateTime(log.created_at)}</p>
                  </div>
                ))
              )}
            </div>
          </Modal>
        </>
      )}

      {activeTab === 'exchange' && <ExchangeConnectionPanel />}
      {activeTab === 'notifications' && <NotificationSettings />}
    </div>
  );
}
